'use client';

import type { FC } from 'react';
import { cn } from '../../utils/cn';
import { UserInline } from '../../components/ui';
import { Credits } from '../../components/Credits';
import { useNavigation } from '../../contexts/NavigationContext';
import type { Bid } from '../../services/generated/Api';

interface BidsListProps {
  bids: Bid[];
  currentUserId: string | null;
}

function formatBidTime(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const BidsList: FC<BidsListProps> = ({ bids, currentUserId }) => {
  const navigation = useNavigation();

  return (
    <div className="overflow-hidden rounded-2xl bg-bg-secondary">
      {bids.map((bid, index) => {
        const isCurrentUser = !!currentUserId && bid.bidder.id === currentUserId;
        const isLatest = index === 0;

        return (
          <button
            key={bid.id}
            type="button"
            onClick={() => navigation.navigate('profile', { userId: bid.bidder.id })}
            className={cn(
              'relative flex w-full items-center gap-3 px-3 py-2.5 text-left transition active:bg-white/[0.04]',
              index < bids.length - 1 && 'border-b border-white/[0.06]',
            )}
          >
            {/* Green left rail for the viewer's own bids. */}
            {isCurrentUser && <span className="absolute inset-y-0 left-0 w-[3px] bg-accent-green" />}

            <UserInline
              userId={bid.bidder.id}
              firstName={bid.bidder.firstName}
              size="s"
              className={cn('min-w-0 flex-1', isCurrentUser && 'font-semibold')}
            />

            <div className="ml-auto flex shrink-0 flex-col items-end">
              <span
                className={cn(
                  'text-sm font-bold tabular-nums',
                  isLatest ? 'text-amber-400' : isCurrentUser ? 'text-accent-green' : 'text-label-primary',
                )}
              >
                <Credits amount={bid.amount} iconSize={13} />
              </span>
              <span className="mt-0.5 text-[11px] text-label-tertiary">{formatBidTime(bid.createdAt)}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
};
